/**
 * CandleScript - Persistent Runner Service
 * Manages applied indicators independently of the builder UI.
 * Re-executes indicators when chart data changes (e.g., timeframe switch).
 */

import {customSignalData} from '../workspace/custom-indicator-signals.mjs';
import { CandleScriptExecutor } from './engine/executor.js';

const APPLIED_KEY = 'candlescript_applied';

class CandleScriptRunner {
    constructor() {
        this._executor = null;
        this._applied = []; // Array of applied indicator configs
        this._dcRef = null; // Current DataCube reference
        this._ready = false;
        this._loadApplied();
    }

    /**
     * Initialize the executor engine
     */
    async init() {
        if (this._executor) return;
        this._executor = new CandleScriptExecutor();
        try {
            await this._executor.init();
            this._ready = true;
        } catch (e) {
            console.warn('[CandleScript Runner] Failed to init executor:', e);
        }
    }

    /**
     * Register an indicator as applied to the chart.
     * Called by ChartIndicatorBuilder when user applies an indicator.
     */
    register(indicatorConfig) {
        const previous=this._applied.find(a=>a.id===indicatorConfig.id || a.name===indicatorConfig.name);
        if(previous?.signalConfig && !indicatorConfig.signalConfig)indicatorConfig={...indicatorConfig,signalConfig:previous.signalConfig};
        // Remove existing with same id or name to prevent duplicates
        this._applied = this._applied.filter(a => a.id !== indicatorConfig.id && a.name !== indicatorConfig.name);
        this._applied.push(indicatorConfig);
        this._saveApplied();
    }

    setSignalConfig(indicatorId,signalConfig) {
        const applied=this._applied.find(item=>item.id===indicatorId);
        if(applied){applied.signalConfig=signalConfig;this._saveApplied()}
    }

    /**
     * Unregister an indicator (removed from chart)
     */
    unregister(indicatorId) {
        this._applied = this._applied.filter(a => a.id !== indicatorId);
        this._saveApplied();
    }

    /**
     * Unregister all indicators by name (for when builder removes by name)
     */
    unregisterByName(name) {
        this._applied = this._applied.filter(a => a.name !== name);
        this._saveApplied();
    }

    /**
     * Unregister all indicators by id or name
     */
    unregisterByIdOrName(id, name) {
        this._applied = this._applied.filter(a => a.id !== id && a.name !== name);
        this._saveApplied();
    }

    removeFromChart(dc, id, name) {
        this.unregisterByIdOrName(id, name);
        this._removeAllOverlaysByIdentity(dc, id, name);
    }

    /**
     * Clear all applied indicators
     */
    clearAll() {
        this._applied = [];
        this._saveApplied();
    }

    /**
     * Get all currently applied indicators
     */
    getApplied() {
        return this._applied;
    }

    /**
     * Check if any indicators are applied
     */
    hasApplied() {
        return this._applied.length > 0;
    }

    /**
     * Re-run all applied indicators on a new DataCube.
     * Called when timeframe changes or data reloads.
     * @param {Object} dc - The new DataCube instance
     * @returns {Promise<void>}
     */
    async reapply(dc) {
        if (!dc || !dc.data || !dc.data.chart || !dc.data.chart.data) return;
        if (!this._applied.length) return;

        if (!this._ready) await this.init();
        if (!this._executor) return;

        this._dcRef = dc;
        const candles = dc.data.chart.data;
        if (!candles || candles.length === 0) return;

        const input = this._extractInput(candles);

        // Remove previous overlays first, then re-apply each indicator.
        for (const config of this._applied) {
            try {
                this._removeAllOverlaysByIdentity(dc, config.id, config.name);

                const result = await this._executor.execute(
                    config.code, input, config.currentValues || {}
                );

                if (result.success && result.result) {
                    this._applyResult(dc, config, result.result, candles);
                }
            } catch (e) {
                console.warn(`[CandleScript Runner] Failed to re-apply "${config.name}":`, e);
            }
        }
    }

    /**
     * Extract OHLCV input from candle data
     */
    _extractInput(candles) {
        const len = candles.length;
        const open = new Float64Array(len);
        const high = new Float64Array(len);
        const low = new Float64Array(len);
        const close = new Float64Array(len);
        const volume = new Float64Array(len);
        for (let i = 0; i < len; i++) {
            open[i] = candles[i][1];
            high[i] = candles[i][2];
            low[i] = candles[i][3];
            close[i] = candles[i][4];
            volume[i] = candles[i][5] || 0;
        }
        return { open, high, low, close, volume };
    }

    /**
     * Apply computed result to the DataCube
     */
    _applyResult(dc, config, result, candles) {
        const side = config.side || 'offchart';
        const arr = dc.data[side];
        if (!arr) return;

        const hasDrawings = result && result.__drawings;
        const dataResult = hasDrawings ? result.__data : result;
        const drawings = hasDrawings ? result.__drawings : [];

        if (dataResult) {
            const outputs = this._parseOutputs(dataResult);
            const plotOutputs = (config.plotConfig && config.plotConfig.outputs) || [];

            if (outputs.length <= 1) {
                const overlayData = this._buildOverlayData(outputs[0] ? outputs[0].data : dataResult, candles);
                const out = plotOutputs[0] || {};
                const overlay = {
                    name: config.name || 'CandleScript',
                    type: 'Spline',
                    data: overlayData,
                    settings: {
                        color: out.color || '#2962ff',
                        lineWidth: out.width || 1.5,
                        candleScriptId: config.id,
                        candleScriptName: config.name || 'CandleScript',
                        group: config.name || 'CandleScript',
                    },
                };
                if (side === 'offchart') overlay.grid = { height: 0.2 };
                arr.push(overlay);
            } else {
                const lineOutputs = outputs.filter((_, i) => (plotOutputs[i] || {}).style !== 'histogram');
                const histOutputs = outputs.filter((_, i) => (plotOutputs[i] || {}).style === 'histogram');
                const visibleOutputs = lineOutputs.length ? lineOutputs : histOutputs.slice(0, 1);
                const visibleIndexes = visibleOutputs.map(o => outputs.indexOf(o));
                const overlay = this._buildCompositeOverlay(
                    visibleOutputs,
                    visibleIndexes.map(i => plotOutputs[i] || {}),
                    candles,
                    config.name || 'CandleScript',
                    side,
                    config
                );
                if (overlay) arr.push(overlay);

                for (let oi = 0; oi < outputs.length; oi++) {
                    if (visibleIndexes.includes(oi)) continue;
                    const aux = this._buildSingleOutputOverlay(outputs[oi], plotOutputs[oi] || {}, candles, config, side, true);
                    if (aux) arr.push(aux);
                }
            }
        }

        // Apply drawing commands
        for (const drawing of drawings) {
            const overlay = this._drawingToOverlay(drawing, candles);
            if (overlay) {
                overlay.settings = {
                    ...(overlay.settings || {}),
                    legend: false,
                    group: config.name || 'CandleScript',
                    candleScriptId: config.id,
                    candleScriptName: config.name || 'CandleScript',
                };
                arr.push(overlay);
            }
        }

        const signal=customSignalData(dataResult,candles,config.plotConfig);
        for(const overlay of arr)if(overlay.settings?.candleScriptId===config.id && overlay.settings.legend!==false){
            overlay.signalData=signal.data;
            overlay.settings.signalOutputs=signal.outputs;
            if(config.signalConfig)overlay.settings.signalConfig=config.signalConfig;
        }
        dc.update_ids();
    }

    /**
     * Parse outputs from result
     */
    _parseOutputs(result) {
        if (Array.isArray(result) || ArrayBuffer.isView(result)) return [{ key: 'value', data: result }];
        if (typeof result === 'object' && result !== null) {
            const outputs = [];
            for (const key of Object.keys(result)) {
                if (Array.isArray(result[key]) || ArrayBuffer.isView(result[key])) {
                    outputs.push({ key, data: result[key] });
                }
            }
            return outputs.length ? outputs : [{ key: 'value', data: result }];
        }
        return [{ key: 'value', data: result }];
    }

    _buildCompositeOverlay(outputs, outputConfigs, candles, name, side, indicatorConfig = {}) {
        if (!outputs.length) return null;
        if (outputs.length === 1) {
            return this._buildSingleOutputOverlay(outputs[0], outputConfigs[0] || {}, candles, { ...indicatorConfig, name }, side, false);
        }
        const data = [];
        for (let i = 0; i < candles.length; i++) {
            const row = [candles[i][0]];
            for (const output of outputs) row.push(this._valueAt(output.data, i));
            data.push(row);
        }
        const overlay = {
            name,
            type: 'Splines',
            data,
            settings: {
                colors: outputConfigs.map(conf => conf.color || '#2962ff'),
                lineWidths: outputConfigs.map(conf => conf.width || 1.5),
                skipNaN: true,
                candleScriptId: indicatorConfig.id,
                candleScriptName: name,
                group: name,
            },
        };
        if (side === 'offchart') overlay.grid = { height: 0.2 };
        return overlay;
    }

    _buildSingleOutputOverlay(output, config, candles, indicatorConfig, side, hidden = false) {
        const data = this._buildOverlayData(output.data, candles);
        if (!data.length) return null;
        const overlay = {
            name: hidden
                ? `${indicatorConfig.name || 'CandleScript'} — ${output.key || 'Aux'}`
                : (indicatorConfig.name || 'CandleScript'),
            type: config.style === 'histogram' ? 'Histogram' : 'Spline',
            data,
            settings: {
                color: config.color || '#2962ff',
                lineWidth: config.width || 1.5,
                legend: hidden ? false : undefined,
                group: indicatorConfig.name || 'CandleScript',
                candleScriptId: indicatorConfig.id,
                candleScriptName: indicatorConfig.name || 'CandleScript',
            },
        };
        if (side === 'offchart') overlay.grid = { height: 0.2 };
        return overlay;
    }

    _valueAt(series, index) {
        const value = series && series[index];
        if (value && typeof value === 'object' && value.value !== undefined) return value.value;
        if (value === undefined || value === null || isNaN(value) || !isFinite(value)) return null;
        return value;
    }

    /**
     * Build overlay data format [[timestamp, value], ...]
     */
    _buildOverlayData(result, candles) {
        const data = [];
        if (Array.isArray(result)) {
            for (let i = 0; i < result.length && i < candles.length; i++) {
                const item = result[i];
                if (item && typeof item === 'object' && item.value !== undefined) {
                    data.push([candles[i][0], item.value]);
                } else if (typeof item === 'number') {
                    data.push([candles[i][0], item]);
                } else {
                    data.push([candles[i][0], null]);
                }
            }
        } else if (ArrayBuffer.isView(result)) {
            for (let i = 0; i < result.length && i < candles.length; i++) {
                data.push([candles[i][0], result[i]]);
            }
        }
        return data;
    }

    /**
     * Convert drawing command to overlay
     */
    _drawingToOverlay(drawing, candles) {
        if (drawing.type === 'line') {
            const data = [];
            const values = drawing.data || [];
            for (let i = 0; i < values.length && i < candles.length; i++) {
                if (values[i] != null && !isNaN(values[i])) {
                    data.push([candles[i][0], values[i]]);
                }
            }
            return {
                name: drawing.title || 'Line',
                type: 'Spline',
                data,
                settings: { color: drawing.color || '#2962ff', lineWidth: drawing.width || 1.5 },
            };
        }
        if (drawing.type === 'hline') {
            // Horizontal line as a constant spline
            const data = candles.map(c => [c[0], drawing.price]);
            return {
                name: drawing.title || 'HLine',
                type: 'Spline',
                data,
                settings: { color: drawing.color || '#787b86', lineWidth: drawing.width || 1 },
            };
        }
        if (drawing.type === 'histogram') {
            const data = [];
            const values = drawing.data || [];
            for (let i = 0; i < values.length && i < candles.length; i++) {
                data.push([candles[i][0], values[i] || 0]);
            }
            return {
                name: drawing.title || 'Histogram',
                type: 'Histogram',
                data,
                settings: { color: drawing.color || '#26a69a' },
            };
        }
        return null;
    }

    /**
     * Remove all overlays with a specific name (both onchart and offchart)
     */
    _removeAllOverlaysByName(dc, name) {
        if (!dc || !dc.data || !name) return;
        const sides = ['onchart', 'offchart'];
        
        for (const side of sides) {
            const arr = dc.data[side];
            if (!arr) continue;
            
            // Remove from end to start to preserve indices
            for (let i = arr.length - 1; i >= 0; i--) {
                const ov = arr[i];
                if (ov && ov.name && (
                    ov.name === name || 
                    ov.name.startsWith(name + ' —') ||
                    ov.name.startsWith(name + ' -')
                )) {
                    arr.splice(i, 1);
                }
            }
        }
        
        dc.update_ids();
    }

    _overlayMatchesIndicator(ov, id, name) {
        if (!ov) return false;
        const settings = ov.settings || {};
        if (id && settings.candleScriptId === id) return true;
        if (name && settings.candleScriptName === name) return true;
        if (name && settings.group === name) return true;
        return !!(name && ov.name && (
            ov.name === name ||
            ov.name.startsWith(name + ' —') ||
            ov.name.startsWith(name + ' -')
        ));
    }

    _removeAllOverlaysByIdentity(dc, id, name) {
        if (!dc || !dc.data) return;
        const sides = ['onchart', 'offchart'];
        for (const side of sides) {
            const arr = dc.data[side];
            if (!arr) continue;
            for (let i = arr.length - 1; i >= 0; i--) {
                if (this._overlayMatchesIndicator(arr[i], id, name)) {
                    arr.splice(i, 1);
                }
            }
        }
        dc.update_ids();
    }

    /**
     * Remove stale empty-data overlays matching a name
     */
    _removeStaleOverlays(dc, name) {
        if (!dc || !dc.data || !name) return;
        const sides = ['onchart', 'offchart'];
        for (const side of sides) {
            const arr = dc.data[side];
            if (!arr) continue;
            for (let i = arr.length - 1; i >= 0; i--) {
                const ov = arr[i];
                if (ov && ov.name && (
                    ov.name.startsWith(name) ||
                    ov.name.startsWith(name + ' —') ||
                    ov.name.startsWith(name + ' -')
                ) && (!ov.data || ov.data.length === 0)) {
                    arr.splice(i, 1);
                }
            }
        }
        dc.update_ids();
    }

    /**
     * Persist applied indicators to localStorage
     */
    _saveApplied() {
        try {
            const data = this._applied.map(a => ({
                id: a.id,
                name: a.name,
                code: a.code,
                side: a.side,
                currentValues: a.currentValues,
                plotConfig: a.plotConfig,
                signalConfig: a.signalConfig,
            }));
            localStorage.setItem(APPLIED_KEY, JSON.stringify(data));
        } catch (e) { /* ignore */ }
    }

    /**
     * Load applied indicators from localStorage
     */
    _loadApplied() {
        try {
            const raw = localStorage.getItem(APPLIED_KEY);
            if (raw) {
                this._applied = JSON.parse(raw);
            }
        } catch (e) {
            this._applied = [];
        }
    }

    /**
     * Destroy the executor
     */
    destroy() {
        if (this._executor) {
            this._executor.destroy();
            this._executor = null;
        }
        this._ready = false;
    }
}

// Singleton instance
let _instance = null;

export function getRunner() {
    if (!_instance) {
        _instance = new CandleScriptRunner();
    }
    return _instance;
}

export default CandleScriptRunner;
